/**
 * <s-filter> — Collapsible filter group with "show more / show less" expand.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --toggle    — Toggles [data-expanded] on [data-body] and flips aria-expanded on the toggle.
 *   --show-more — Toggles the .hidden class on [data-filter-hidden] items and swaps the
 *                  button label between data-show-more-text / data-show-less-text.
 *
 * Required child markup:
 *   [data-body]              — Container whose [data-expanded] attribute reflects open/closed.
 *   [command="--toggle"]     — The toggle button (also the source for --toggle commands).
 *                               Its [aria-hidden] icon (if any) is rotated 180° when open.
 *   [data-show-more]         — The "show more / less" button; its textContent is swapped.
 *   [data-filter-hidden]     — Items initially collapsed; toggled via .hidden class.
 *
 * Element dataset (on <s-filter> itself):
 *   data-show-more-text — i18n string for the collapsed state (e.g. "Show more").
 *   data-show-less-text — i18n string for the expanded state (e.g. "Show less").
 */
class SFilter extends HTMLElement {
  #expanded = false;
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (e) => {
      if (e.command === '--toggle') this.#handleToggle();
      else if (e.command === '--show-more') this.#handleShowMore();
    }, opts);
  }

  #handleToggle() {
    const body = this.querySelector('[data-body]');
    const toggle = this.querySelector('[command="--toggle"]');
    if (!body || !toggle) return;

    const isExpanded = body.hasAttribute('data-expanded');
    if (isExpanded) {
      body.removeAttribute('data-expanded');
    } else {
      body.setAttribute('data-expanded', '');
    }
    toggle.setAttribute('aria-expanded', String(!isExpanded));
    const icon = toggle.querySelector('[aria-hidden]');
    if (icon) icon.style.transform = isExpanded ? '' : 'rotate(180deg)';
  }

  #handleShowMore() {
    const showMore = this.querySelector('[data-show-more]');
    if (!showMore) return;

    this.#expanded = !this.#expanded;
    for (const item of this.querySelectorAll('[data-filter-hidden]')) {
      item.classList.toggle('hidden', !this.#expanded);
    }
    showMore.textContent = this.#expanded
      ? this.dataset.showLessText
      : this.dataset.showMoreText;
    showMore.setAttribute('aria-expanded', String(this.#expanded));
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

customElements.define('s-filter', SFilter);
